// Typed PlatformAPI client. Every route under /api/v1/platform that PlatformFE
// consumes lives here (see tmp/platformapi-contract-catalog.md for the source
// contract; this module mirrors it one-to-one).

import { http } from '../http/client';
import type {
  ActivityRow,
  DestinationOrganization,
  AuthorizationSnapshot,
  FeedPage,
  LauncherProduct,
  LoginStep1Response,
  MembershipRow,
  NotificationRow,
  Organization,
  PostVisibility,
  ProductCode,
  Profile,
  ResourceCatalogRow,
  ResourceGrantInput,
  RoleGrantRow,
  RoleRow,
  SessionRow,
  TokenPair,
} from './types';

interface CreatedResponse {
  id: string;
  createdAt?: string;
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export interface RegisterInput {
  challengeId: string;
  code: string;
  email: string;
  userName: string;
  password: string;
  firstName: string;
  lastName: string;
}

export const authApi = {
  async requestOtp(email: string, purpose: 'Registration' | 'PasswordReset'): Promise<{ challengeId: string }> {
    const { data } = await http().post<{ challengeId: string }>('/auth/otp/request', { email, purpose });
    return data;
  },
  async resetPassword(input: { email: string; challengeId: string; code: string; password: string }): Promise<void> {
    await http().post('/auth/password/reset', input);
  },
  async destinations(): Promise<{ organizations: DestinationOrganization[] }> {
    const { data } = await http().get<{ organizations: DestinationOrganization[] }>('/auth/destinations');
    return data;
  },
  /** Public sign-up into the COMMUNITY organization (PlatformDB script 027). */
  async register(input: RegisterInput): Promise<{ userId: string; organizationId: string }> {
    const { data } = await http().post<{ userId: string; organizationId: string }>('/auth/register', input);
    return data;
  },
  async createProductLaunch(productCode: ProductCode): Promise<{ code: string }> {
    const { data } = await http().post<{ code: string }>('/auth/product-launch', { productCode });
    return data;
  },
  async loginStep1(login: string, password: string): Promise<LoginStep1Response> {
    const { data } = await http().post<LoginStep1Response>('/auth/login', { login, password });
    return data;
  },

  async loginStep2(
    login: string,
    password: string,
    organizationId: string,
    productCode: ProductCode,
  ): Promise<TokenPair> {
    const { data } = await http().post<TokenPair>('/auth/login', {
      login,
      password,
      organizationId,
      productCode,
    });
    return data;
  },

  async logout(refreshToken: string): Promise<void> {
    await http().post('/auth/logout', { refreshToken }, { skipErrorToast: true });
  },

  async sessions(): Promise<SessionRow[]> {
    const { data } = await http().get<SessionRow[]>('/auth/sessions');
    return data;
  },

  async revokeSession(sessionId: string): Promise<void> {
    await http().delete(`/auth/sessions/${sessionId}`);
  },
};

// ---------------------------------------------------------------------------
// Profile, organization, memberships, launcher
// ---------------------------------------------------------------------------

export interface ProfileUpdate {
  firstName: string;
  lastName: string;
  phone?: string | null;
}

export interface OrganizationUpdate {
  name: string;
  registrationNumber?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  // Legacy Company parity (PlatformDB script 017). A null clears the column, which is what
  // the screen sends when the operator empties an input. Time zone, currency, VAT, bank %,
  // menu key, license, working hours and the directory flag are edited per business on the
  // organization profiles.
  location?: string | null;
  description?: string | null;
  website?: string | null;
  languageCode?: string | null;
  fax?: string | null;
}

export interface MembershipCreate {
  email: string;
  productCodes?: string[];
  roleCodes?: string[];
}

export interface MembershipUpdate {
  status: 'Active' | 'Suspended' | 'Revoked';
  productCodes?: string[];
  roleCodes?: string[];
}

export const platformApi = {
  async profile(): Promise<Profile> {
    const { data } = await http().get<Profile>('/me');
    return data;
  },

  async updateProfile(input: ProfileUpdate): Promise<void> {
    await http().put('/me', input);
  },

  async organization(): Promise<Organization> {
    const { data } = await http().get<Organization>('/organization');
    return data;
  },

  async updateOrganization(input: OrganizationUpdate): Promise<void> {
    await http().put('/organization', input);
  },

  async memberships(): Promise<MembershipRow[]> {
    const { data } = await http().get<MembershipRow[]>('/organization/memberships');
    return data;
  },

  async createMembership(input: MembershipCreate): Promise<CreatedResponse> {
    const { data } = await http().post<CreatedResponse>('/organization/memberships', input);
    return data;
  },

  async updateMembership(membershipId: string, input: MembershipUpdate): Promise<void> {
    await http().put(`/organization/memberships/${membershipId}`, input);
  },

  async launcher(): Promise<LauncherProduct[]> {
    const { data } = await http().get<LauncherProduct[]>('/launcher');
    return data;
  },
};

// ---------------------------------------------------------------------------
// Authorization
// ---------------------------------------------------------------------------

export interface RoleInput {
  code: string;
  name: string;
  description?: string;
  copyFromRoleId?: string;
}

export const authorizationApi = {
  async snapshot(): Promise<AuthorizationSnapshot> {
    const { data } = await http().get<AuthorizationSnapshot>('/authorization/snapshot');
    return data;
  },

  async resources(productCode: ProductCode): Promise<ResourceCatalogRow[]> {
    const { data } = await http().get<ResourceCatalogRow[]>('/authorization/resources', {
      params: { productCode },
    });
    return data;
  },

  async roles(): Promise<RoleRow[]> {
    const { data } = await http().get<RoleRow[]>('/authorization/roles');
    return data;
  },

  async createRole(input: RoleInput): Promise<CreatedResponse> {
    const { data } = await http().post<CreatedResponse>('/authorization/roles', input);
    return data;
  },

  async updateRole(roleId: string, input: RoleInput): Promise<void> {
    await http().put(`/authorization/roles/${roleId}`, input);
  },
  async deleteRole(roleId: string): Promise<void> {
    await http().delete(`/authorization/roles/${roleId}`);
  },

  async roleGrants(roleId: string, productCode: ProductCode): Promise<RoleGrantRow[]> {
    const { data } = await http().get<RoleGrantRow[]>(`/authorization/roles/${roleId}/grants`, {
      params: { productCode },
    });
    return data;
  },

  async saveRoleGrants(
    roleId: string,
    productCode: ProductCode,
    grants: ResourceGrantInput[],
  ): Promise<void> {
    await http().put(`/authorization/roles/${roleId}/grants`, { productCode, grants });
  },
};

// ---------------------------------------------------------------------------
// Feed
// ---------------------------------------------------------------------------

export const feedApi = {
  async feed(cursor?: string | null, pageSize = 20): Promise<FeedPage> {
    const { data } = await http().get<FeedPage>('/feed/', { params: { cursor, pageSize } });
    return data;
  },

  /** `mediaIds`: photos already uploaded to the Media Server (feed `posts/media/upload-session`); the API commits them. */
  async createPost(content: string, visibility: PostVisibility, mediaIds: string[] = []): Promise<CreatedResponse> {
    const { data } = await http().post<CreatedResponse>('/feed/posts', { content, visibility, mediaIds });
    return data;
  },

  async deletePost(postId: string): Promise<void> {
    await http().delete(`/feed/posts/${postId}`);
  },

  async addComment(postId: string, content: string): Promise<CreatedResponse> {
    const { data } = await http().post<CreatedResponse>(`/feed/posts/${postId}/comments`, { content });
    return data;
  },

  async like(postId: string): Promise<void> {
    await http().post(`/feed/posts/${postId}/likes`);
  },

  async unlike(postId: string): Promise<void> {
    await http().delete(`/feed/posts/${postId}/likes`);
  },
};

// ---------------------------------------------------------------------------
// Notifications and activity
// ---------------------------------------------------------------------------

export const notificationsApi = {
  async list(unreadOnly = false, pageSize = 50): Promise<NotificationRow[]> {
    const { data } = await http().get<NotificationRow[]>('/notifications/', {
      params: { unreadOnly, pageSize },
    });
    return data;
  },

  async markRead(notificationId: string): Promise<void> {
    await http().put(`/notifications/${notificationId}/read`);
  },
};

export const activityApi = {
  async list(pageSize = 50): Promise<ActivityRow[]> {
    const { data } = await http().get<ActivityRow[]>('/activity', { params: { pageSize } });
    return data;
  },
};
